import * as React from "react";
import { useLocation } from "wouter";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail } from "lucide-react";
import { authService } from "@/services/authService";

type ForgotOutcome =
  | "email_generic"
  | "email_sent"
  | "callsign_not_found"
  | "use_iar_no_email"
  | null;

export function ForgotPasswordPage() {
  const [, setLocation] = useLocation();
  const [identifier, setIdentifier] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [outcome, setOutcome] = React.useState<ForgotOutcome>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setMessage(null);
    setOutcome(null);
    setIsSubmitting(true);

    try {
      const { message: msg, outcome: oc } = await authService.requestPasswordReset(identifier);
      setMessage(msg);
      setOutcome(oc);
    } catch (error) {
      console.error("Failed to request password reset:", error);
      setErrorMessage("Terjadi kesalahan. Silakan coba lagi.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToIarLogin = () => {
    const cs = identifier.trim().toUpperCase();
    const q = new URLSearchParams();
    q.set("from", "forgot-no-email");
    if (cs) q.set("callsign", cs);
    setLocation(`/login?${q.toString()}`);
  };

  return (
    <Card className="w-full shadow-lg border-border/50">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Lupa Password</CardTitle>
        <CardDescription className="text-muted-foreground space-y-2">
          <span className="block">
            <strong>Email:</strong> jika akun memakai email yang sama, kami kirim tautan reset (pesan samar jika email
            tidak terdaftar).
          </span>
          <span className="block">
            <strong>Callsign:</strong> kami cek apakah ada email di akun — jika tidak, Anda harus masuk dengan{" "}
            <strong>IAR</strong> lalu mengatur kata sandi.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="identifier">Email atau Callsign</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="identifier"
                type="text"
                placeholder="contoh: nama@email.com atau YB0XXX"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                disabled={isSubmitting}
                className="pl-10"
              />
            </div>
          </div>

          {message && outcome === "email_sent" && (
            <div className="bg-green-50 text-green-800 text-sm p-3 rounded-lg border border-green-200 whitespace-pre-line dark:bg-green-950/50 dark:text-green-200 dark:border-green-800">
              {message}
            </div>
          )}

          {message && outcome === "email_generic" && (
            <div className="bg-amber-50 text-amber-900 text-sm p-3 rounded-lg border border-amber-200 whitespace-pre-line dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800">
              {message}
            </div>
          )}

          {message && outcome === "callsign_not_found" && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20 whitespace-pre-line">
              {message}
            </div>
          )}

          {message && outcome === "use_iar_no_email" && (
            <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950 whitespace-pre-line dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-200">
              {message}
              <Button type="button" className="w-full" onClick={goToIarLogin}>
                Lanjut ke Masuk dengan IAR
              </Button>
            </div>
          )}

          {errorMessage && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
              {errorMessage}
            </div>
          )}

          <div className="space-y-3">
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Memproses..." : "Lanjutkan"}
            </Button>
            <button
              type="button"
              onClick={() => setLocation("/login")}
              className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5 underline underline-offset-4 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Kembali ke Masuk
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
